import { Box } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import GalleryVideoCard, { type GalleryVideo } from "./GalleryVideoCard";

interface GalleryGridProps {
  videos: GalleryVideo[];
  displayMode: string;
  gridColumns: number;
  gridRows: number;
  autoplay: boolean;
  muted: boolean;
  qrEnabled: boolean;
  qrPosition: string;
  qrSize: string;
  showHeader: boolean;
  allowDownload?: boolean;
  gallerySlug?: string;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (videoId: string) => void;
  onVideoClick?: (video: GalleryVideo) => void;
  hasBackground?: boolean;
}

const cardVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
};

export default function GalleryGrid({
  videos,
  displayMode,
  gridColumns,
  gridRows,
  autoplay,
  muted,
  qrEnabled,
  qrPosition,
  qrSize,
  showHeader,
  allowDownload = false,
  gallerySlug,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
  onVideoClick,
  hasBackground = false,
}: GalleryGridProps) {
  const isFixed = displayMode === "fixed";
  const maxVideos = isFixed ? gridColumns * gridRows : videos.length;
  const visibleVideos = videos.slice(0, maxVideos);
  const shouldReduceMotion = useReducedMotion();

  // In fixed mode, take up the full viewport minus optional header
  const headerHeight = showHeader ? 56 : 0;

  // Responsive columns: cap on smaller screens
  const responsiveColumns = {
    xs: 1,
    sm: Math.min(gridColumns, 3),
    md: gridColumns,
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: `repeat(${responsiveColumns.xs}, 1fr)`,
          sm: `repeat(${responsiveColumns.sm}, 1fr)`,
          md: `repeat(${responsiveColumns.md}, 1fr)`,
        },
        ...(isFixed
          ? {
              gridTemplateRows: `repeat(${gridRows}, 1fr)`,
              height: `calc(100vh - ${headerHeight}px)`,
              overflow: "hidden",
            }
          : {
              gridAutoRows: "minmax(200px, 1fr)",
              minHeight: `calc(100vh - ${headerHeight}px)`,
              overflow: "auto",
            }),
        gap: { xs: 1, sm: 2 },
        p: { xs: 1, sm: 2 },
        mt: showHeader ? `${headerHeight}px` : 0,
        ...(hasBackground && {
          backdropFilter: "blur(8px)",
          borderRadius: 2,
        }),
      }}
    >
      <AnimatePresence mode="popLayout">
        {visibleVideos.map((video) => (
          <motion.div
            key={video.id}
            layoutId={video.id}
            layout={!shouldReduceMotion}
            variants={shouldReduceMotion ? undefined : cardVariants}
            initial={shouldReduceMotion ? false : "initial"}
            animate="animate"
            exit={shouldReduceMotion ? undefined : "exit"}
            transition={
              shouldReduceMotion
                ? { duration: 0 }
                : { duration: 0.3, ease: "easeOut" }
            }
            style={{ width: "100%", height: "100%" }}
          >
            <GalleryVideoCard
              video={video}
              autoplay={autoplay}
              muted={muted}
              qrEnabled={qrEnabled}
              qrPosition={qrPosition}
              qrSize={qrSize}
              allowDownload={allowDownload}
              gallerySlug={gallerySlug}
              selectionMode={selectionMode}
              selected={selectedIds?.has(video.id) ?? false}
              onToggleSelect={onToggleSelect}
              onClick={onVideoClick}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </Box>
  );
}
